import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import classicBackground from "./assets/templates/classic.svg";
import modernBackground from "./assets/templates/modern.svg";
import newBackground from "./assets/templates/new.svg";

// Shared renderer for a certificate template - used by the Settings >
// Course > Certificate list cards and the Adding/Editing Certificate
// canvas, so both show exactly the same thing (Figma renders every card as
// the full certificate, not just its background).
//
// The certificate is laid out once on a fixed 800x560 design stage and then
// scaled to whatever width it's given, so the text keeps the same
// proportions in a 335px card and a 850px editor canvas.

const DESIGN_WIDTH = 800;
const DESIGN_HEIGHT = 560;

const navy = "#1F243B";
const slate = "#636980";

export const DEFAULT_ALIGNMENT = "center-1";

export const ALIGNMENT_OPTIONS = [
  { value: "center-1", label: "Centre aligned - 1" },
  { value: "center-2", label: "Centre aligned - 2" },
  { value: "left-1", label: "Left aligned - 1" },
  { value: "left-2", label: "Left aligned - 2" },
  { value: "left-3", label: "Left aligned - 3" },
  { value: "right-1", label: "Right aligned - 1" },
  { value: "right-2", label: "Right aligned - 2" },
  { value: "right-3", label: "Right aligned - 3" },
];

// Positions are percentages of the design stage, measured from the Figma
// frames. The numbered variants mostly differ in how the dates are laid out:
//   stacked-sides - "(Issue date)" over "Issued Date", one block per side
//   inline-sides  - "Issued Date (Issue date)" ... "Expiry Date (Expiry date)"
//   stacked-start / stacked-end - both inline dates stacked on one side
const LAYOUTS = {
  "center-1": {
    text: { left: 15, width: 70, top: 18, align: "center" },
    dates: { mode: "stacked-sides", left: 22, right: 22, bottom: 15 },
  },
  "center-2": {
    text: { left: 15, width: 70, top: 21, align: "center" },
    dates: { mode: "inline-sides", left: 16, right: 16, bottom: 19 },
  },
  "left-1": {
    text: { left: 11, width: 65, top: 18, align: "left" },
    dates: { mode: "stacked-sides", left: 11, right: 11, bottom: 15 },
  },
  "left-2": {
    text: { left: 11, width: 65, top: 21, align: "left" },
    dates: { mode: "inline-sides", left: 11, right: 11, bottom: 19 },
  },
  "left-3": {
    text: { left: 11, width: 65, top: 18, align: "left" },
    dates: { mode: "stacked-start", left: 11, right: 11, bottom: 15 },
  },
  "right-1": {
    text: { left: 24, width: 65, top: 18, align: "right" },
    dates: { mode: "stacked-sides", left: 11, right: 11, bottom: 15 },
  },
  "right-2": {
    text: { left: 24, width: 65, top: 21, align: "right" },
    dates: { mode: "inline-sides", left: 11, right: 11, bottom: 19 },
  },
  "right-3": {
    text: { left: 24, width: 65, top: 18, align: "right" },
    dates: { mode: "stacked-end", left: 11, right: 11, bottom: 15 },
  },
};

// The three sample certificates from Figma (Classic / Modern / New) are
// seeded per client by the backend with background_image = "template:<id>"
// instead of a URL - the images ship with the frontend bundle, so their
// hashed URLs can't be stored. Everything else is an uploaded S3 URL.
const BUILT_IN_BACKGROUNDS = {
  "template:classic": classicBackground,
  "template:modern": modernBackground,
  "template:new": newBackground,
};

export const resolveBackground = (value) => BUILT_IN_BACKGROUNDS[value] || value || null;

// The built-in artwork isn't symmetric, so each sample gets its own safe
// text area (% of the stage kept clear on each side), measured from the
// Figma cards: Classic's text starts at 12.4% and its blue panels take the
// right third; New's text starts at 7.6% and the green arc takes the right
// ~38%. Modern's frame is symmetric, so it keeps the default LAYOUTS
// positions, as do uploaded backgrounds.
const TEMPLATE_INSETS = {
  "template:classic": { left: 12.4, right: 33 },
  "template:new": { left: 7.6, right: 38 },
};

// Re-positions a layout inside a template's safe area: left/right-aligned
// text hugs the matching edge, centred text centres within the area, and
// the dates span the same area (inset a little more when centred, like the
// default centre layouts).
const applyInsets = (layout, insets) => {
  if (!insets) return layout;
  const { left, right } = insets;
  const span = 100 - left - right;
  const width = Math.min(layout.text.width, span);
  const { align } = layout.text;

  let textLeft = left + (span - width) / 2;
  if (align === "left") textLeft = left;
  if (align === "right") textLeft = 100 - right - width;

  const datesInset = align === "center" ? span * 0.08 : 0;
  return {
    text: { ...layout.text, left: textLeft, width },
    dates: { ...layout.dates, left: left + datesInset, right: right + datesInset },
  };
};

// Older rows / the removed Alignmentlayouts.js used "centre-N".
export const normalizeAlignment = (value) => {
  const normalized = (value || "").replace(/^centre/, "center");
  return LAYOUTS[normalized] ? normalized : DEFAULT_ALIGNMENT;
};

const Frame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${DESIGN_WIDTH} / ${DESIGN_HEIGHT};
  overflow: hidden;
  background: ${(props) => (props.$hasBackground ? "#ffffff" : "#F5F7FA")};
`;

const Background = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
  user-select: none;
`;

const Stage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${DESIGN_WIDTH}px;
  height: ${DESIGN_HEIGHT}px;
  transform-origin: top left;
  pointer-events: none;
  color: ${navy};
`;

const TextBlock = styled.div`
  position: absolute;
  transition: left 0.25s ease, top 0.25s ease;
`;

const OrgLogo = styled.div`
  font-size: 12px;
  color: ${slate};
  margin-bottom: 26px;
`;

const Title = styled.div`
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 22px;
  word-break: break-word;
`;

const Subtitle = styled.div`
  font-size: 11px;
  color: ${slate};
  margin-bottom: 12px;
`;

const Recipient = styled.div`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 26px;
`;

const CourseName = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const DatesRow = styled.div`
  position: absolute;
  display: flex;
  font-size: 11px;
  line-height: 1.6;
`;

const DateValue = styled.span`
  font-weight: 600;
  color: ${navy};
`;

const DateLabel = styled.span`
  color: ${slate};
`;

const StackedDate = ({ value, label, align }) => (
  <div style={{ textAlign: align }}>
    <div>
      <DateValue>{value}</DateValue>
    </div>
    <div>
      <DateLabel>{label}</DateLabel>
    </div>
  </div>
);

const InlineDate = ({ value, label }) => (
  <div>
    <DateLabel>{label}</DateLabel>&nbsp;&nbsp;<DateValue>{value}</DateValue>
  </div>
);

const Dates = ({ layout }) => {
  const { mode, left, right, bottom } = layout;
  const box = { left: `${left}%`, right: `${right}%`, bottom: `${bottom}%` };

  if (mode === "stacked-start" || mode === "stacked-end") {
    return (
      <DatesRow
        style={{
          ...box,
          flexDirection: "column",
          alignItems: mode === "stacked-start" ? "flex-start" : "flex-end",
        }}
      >
        <InlineDate label="Issued Date" value="(Issue date)" />
        <InlineDate label="Expiry Date" value="(Expiry date)" />
      </DatesRow>
    );
  }

  const Item = mode === "inline-sides" ? InlineDate : StackedDate;
  return (
    <DatesRow style={{ ...box, justifyContent: "space-between", alignItems: "flex-end" }}>
      <Item label="Issued Date" value="(Issue date)" align="left" />
      <Item label="Expiry Date" value="(Expiry date)" align="right" />
    </DatesRow>
  );
};

export default function CertificatePreview({
  backgroundImage,
  certificateType,
  alignment,
  className,
  children,
}) {
  const frameRef = useRef(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const update = () => setScale(frame.clientWidth / DESIGN_WIDTH);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const layout = applyInsets(
    LAYOUTS[normalizeAlignment(alignment)],
    TEMPLATE_INSETS[backgroundImage]
  );
  const { text } = layout;
  const backgroundSrc = resolveBackground(backgroundImage);

  return (
    <Frame ref={frameRef} className={className} $hasBackground={!!backgroundSrc}>
      {backgroundSrc && <Background src={backgroundSrc} alt="" />}

      <Stage style={{ transform: `scale(${scale})` }}>
        <TextBlock
          style={{
            left: `${text.left}%`,
            width: `${text.width}%`,
            top: `${text.top}%`,
            textAlign: text.align,
          }}
        >
          {/* Placeholders - filled with the org's logo and the learner /
              course details when a certificate is actually issued. */}
          <OrgLogo>(Organization Logo)</OrgLogo>
          <Title>{certificateType || "Certificate of Completion"}</Title>
          <Subtitle>This Certification is awarded to</Subtitle>
          <Recipient>(First name) (Last name)</Recipient>
          <Subtitle>For successfully completing the course</Subtitle>
          <CourseName>(Course Name)</CourseName>
        </TextBlock>

        <Dates layout={layout.dates} />
      </Stage>

      {children}
    </Frame>
  );
}
